import type { UIMessage, UIMessagePart } from 'ai';

/**
 * FRONT-855 (Task 3) — pure decision logic for the "compose gap" shimmer.
 *
 * The gap: once a tool call (e.g. a delegation) finishes, there's a real
 * pause before the model starts streaming the final answer text. Today that
 * window renders nothing. This function decides when to show a shimmer
 * placeholder in that specific window — and ONLY that window.
 *
 * Deliberately mirrors the "has visible content" shape used by
 * `lastAssistantHasNoVisibleContent` in chat-messages.tsx (same part-type
 * checks) rather than reinventing it, so the two derivations can never
 * silently drift out of sync with each other.
 *
 * Mutual exclusivity with the existing `isWaiting` ("Thinking…") state falls
 * out of the shapes themselves, not from extra bookkeeping: `isWaiting`'s
 * `lastAssistantHasNoVisibleContent` treats ANY tool part (running or
 * completed) as visible content, so it is false the moment a tool part
 * exists. This function requires a tool part to exist (and be completed) —
 * so it is false whenever `isWaiting` would be true, and vice versa a
 * completed tool part with no text always makes `isWaiting` false. The two
 * can never both be true for the same message.
 */

type AnyPart = UIMessagePart<any, any>;

function isNonEmptyTextOrReasoning(p: AnyPart): boolean {
  return (
    (p.type === 'text' && p.text.length > 0) ||
    (p.type === 'reasoning' && p.text.length > 0)
  );
}

function isToolPart(p: AnyPart): boolean {
  return p.type === 'dynamic-tool' || (typeof p.type === 'string' && p.type.startsWith('tool-'));
}

const RUNNING_TOOL_STATES = new Set(['input-streaming', 'input-available']);

function isCompletedToolPart(p: AnyPart): boolean {
  if (!isToolPart(p)) return false;
  const state = (p as { state?: string }).state;
  // A tool part with no `state` field (shouldn't happen post-SDK-v5, but
  // guard defensively) is treated as not-yet-completed.
  if (!state) return false;
  return !RUNNING_TOOL_STATES.has(state);
}

/** Only the part types that ever render something in the transcript — mirrors
 * the check in chat-messages.tsx's `lastAssistantHasNoVisibleContent`. */
function isVisiblePart(p: AnyPart): boolean {
  return isNonEmptyTextOrReasoning(p) || isToolPart(p);
}

/**
 * True only when: the stream is actively `'streaming'`, the last assistant
 * message's last VISIBLE part is a completed (non-running) tool call, and no
 * non-empty text/reasoning part exists yet anywhere in that message.
 */
export function isComposingAfterTool(
  status: string,
  lastAssistantMessage: UIMessage | undefined,
): boolean {
  if (status !== 'streaming') return false;
  if (!lastAssistantMessage || lastAssistantMessage.role !== 'assistant') return false;

  const parts = lastAssistantMessage.parts;
  const visibleParts = parts.filter(isVisiblePart);
  const lastVisible = visibleParts[visibleParts.length - 1];
  if (!lastVisible) return false;
  if (!isCompletedToolPart(lastVisible)) return false;

  const hasText = parts.some(isNonEmptyTextOrReasoning);
  return !hasText;
}
